import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Link as LinkIcon } from 'lucide-react'
import { Card, ErrorBox, Loading, Empty } from './ui'
import { updatePnlAccount, refreshPnl, reconnectXeroForPnl } from '../../lib/pnlApi'
import { PNL_KEY } from './queries'
import { errMsg, whenAu, dayLabel } from './format'
import { businessToday, monthLabel } from '../../lib/dates'

// What each mapping line means on the statement. Unknown keys from the API
// fall back to their own name.
const LINE_LABELS = {
  from_portal:   'Skip: comes from the portal',
  cost_of_sales: 'Cost of sales',
  wages:         'Wages',
  super:         'Superannuation',
  overhead:      'Overheads',
  excluded:      'Left out: below PBIT',
}

const PNL_SCOPES = [
  ['accounting.reports.profitandloss.read', 'Profit & Loss report'],
  ['accounting.settings.read', 'Chart of accounts'],
  ['payroll.timesheets.read', 'Payroll timesheets'],
]

const BACKFILL_FROM = '2026-04'

function lineOptions(lines) {
  const list = (lines && lines.length ? lines : Object.keys(LINE_LABELS))
  return list.map((l) => (typeof l === 'string'
    ? { key: l, label: LINE_LABELS[l] || l }
    : { key: l.key ?? l.line ?? l.value, label: l.label || LINE_LABELS[l.key ?? l.line ?? l.value] || (l.key ?? l.line) }))
}

function XeroCard({ statusQ, addToast }) {
  const [going, setGoing] = useState(false)
  const s = statusQ.data
  async function reconnect() {
    setGoing(true)
    try {
      await reconnectXeroForPnl()
    } catch (e) {
      setGoing(false)
      addToast({ type: 'error', message: `Couldn't start the Xero reconnect: ${errMsg(e)}` })
    }
  }
  const missing = new Set(s?.missing_scopes || [])
  return (
    <Card
      title="Xero connection"
      sub="The P&L needs read-only access to your Xero P&L report, chart of accounts and payroll timesheets, on top of invoices and contacts."
    >
      {statusQ.error ? <ErrorBox error={statusQ.error} what="Xero status" />
        : statusQ.isLoading && !s ? <Loading />
          : (
            <>
              <ul className="text-[12.5px] leading-relaxed space-y-0.5">
                <li className={s?.xero_connected ? 'text-slate-700' : 'text-red-600'}>
                  {s?.xero_connected ? '✔' : '✖'} Connected to Xero
                </li>
                {PNL_SCOPES.map(([scope, label]) => {
                  const ok = s?.xero_connected && !missing.has(scope)
                  return <li key={scope} className={ok ? 'text-slate-700' : 'text-red-600'}>{ok ? '✔' : '✖'} {label}</li>
                })}
                {[...missing].filter((m) => !PNL_SCOPES.some(([sc]) => sc === m)).map((m) => (
                  <li key={m} className="text-red-600">✖ {m}</li>
                ))}
              </ul>
              <dl className="text-xs text-slate-500 mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                <dt>Last refresh</dt><dd>{whenAu(s?.last_refresh) || '—'}</dd>
                <dt>Portal figures to</dt><dd>{s?.daily_computed_to ? dayLabel(s.daily_computed_to) : '—'}</dd>
                <dt>Timesheets to</dt><dd>{s?.timesheets_to ? dayLabel(s.timesheets_to) : '—'}</dd>
                <dt>Xero P&amp;L months</dt>
                <dd>{(s?.xero_months || []).length ? s.xero_months.map((m) => monthLabel(m, true)).join(', ') : '—'}</dd>
              </dl>
            </>
          )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-primary !py-1.5" onClick={reconnect} disabled={going}>
          <LinkIcon size={14} /> {going ? 'Opening Xero…' : 'Reconnect Xero'}
        </button>
        <span className="text-xs text-slate-500">Read-only. Keeps your invoice settings (it doesn&apos;t disconnect first).</span>
      </div>
    </Card>
  )
}

function RuleCard({ addToast }) {
  const qc = useQueryClient()
  const toMonth = businessToday().slice(0, 7)
  const run = useMutation({
    mutationFn: () => refreshPnl({ from_month: BACKFILL_FROM, to_month: toMonth }),
    onSuccess: (data) => {
      if (data && data.accepted === false) {
        addToast({ type: 'info', message: data.detail || 'A recalculation is already running. Press Refresh in a few minutes.' })
      } else {
        addToast({ type: 'success', message: 'Recalculating from April 2026 in the background. Press Refresh in a few minutes.' })
      }
      qc.invalidateQueries({ queryKey: [...PNL_KEY, 'status'] })
    },
    onError: (e) => addToast({ type: 'error', message: `Couldn't start the recalculation: ${errMsg(e)}` }),
  })
  return (
    <Card title="How costs are split">
      <div className="text-[12.5px] bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-700 leading-relaxed">
        Each day&apos;s wages and overheads are split across clients by their share of that day&apos;s orders. A client
        with 10 of the day&apos;s 100 orders carries 10% of that day&apos;s costs.
      </div>
      <p className="text-[12.5px] text-slate-500 mt-2.5">
        <b>Who can see this page:</b> only logins on the P&amp;L list, because it shows wages.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-secondary !py-1.5" onClick={() => run.mutate()} disabled={run.isPending}>
          <RefreshCw size={14} className={run.isPending ? 'animate-spin' : ''} /> Recalculate history
        </button>
        <span className="text-xs text-slate-500">Recomputes every day from April 2026 to now. The hourly job only covers this month and last.</span>
      </div>
    </Card>
  )
}

function MappingCard({ accountsQ, addToast }) {
  const qc = useQueryClient()
  const [saving, setSaving] = useState(null)
  const save = useMutation({
    mutationFn: ({ accountId, line }) => updatePnlAccount(accountId, line),
    onMutate: ({ accountId }) => setSaving(accountId),
    onSuccess: (_d, { name, line }) => {
      addToast({ type: 'success', message: `${name} now shows as “${LINE_LABELS[line] || line}”` })
      qc.invalidateQueries({ queryKey: PNL_KEY })
    },
    onError: (e) => addToast({ type: 'error', message: `Couldn't save the mapping: ${errMsg(e)}` }),
    onSettled: () => setSaving(null),
  })

  const { data, error, isLoading } = accountsQ
  const opts = lineOptions(data?.lines)
  const accounts = [...(data?.accounts || [])].sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''), 'en', { numeric: true }))

  return (
    <Card
      title="Xero accounts → P&L lines"
      sub="Set once. Changes apply to every period straight away. “Default” means the line was guessed from the account's Xero type and name."
    >
      {error ? <ErrorBox error={error} what="Xero accounts" />
        : isLoading && !data ? <Loading />
          : !accounts.length ? <Empty>No Xero accounts fetched yet. Reconnect Xero, then wait for the hourly refresh.</Empty>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-slate-300 text-xs text-slate-500">
                      <th className="text-left font-semibold px-2.5 py-2">Code</th>
                      <th className="text-left font-semibold px-2.5 py-2">Xero account</th>
                      <th className="text-left font-semibold px-2.5 py-2 hidden sm:table-cell">Type</th>
                      <th className="text-left font-semibold px-2.5 py-2">Shows on the P&amp;L as</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((a) => (
                      <tr key={a.account_id} className="border-b border-slate-100">
                        <td className="px-2.5 py-1.5 tabular-nums">{a.code || '—'}</td>
                        <td className="px-2.5 py-1.5">{a.name}</td>
                        <td className="px-2.5 py-1.5 text-slate-500 text-xs hidden sm:table-cell">{a.type}</td>
                        <td className="px-2.5 py-1.5">
                          <div className="flex items-center gap-2">
                            <select
                              className="border border-slate-300 rounded-md px-2 py-1 text-[13px] bg-white max-w-[230px]"
                              value={a.line || ''}
                              disabled={saving === a.account_id}
                              onChange={(e) => save.mutate({ accountId: a.account_id, line: e.target.value, name: a.name })}
                              aria-label={`P&L line for ${a.name}`}
                            >
                              {!a.line && <option value="">—</option>}
                              {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                            </select>
                            {a.is_default && <span className="text-[10px] px-1.5 py-px rounded-full bg-slate-100 text-slate-500">default</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
    </Card>
  )
}

export default function SetupTab({ statusQ, accountsQ, addToast }) {
  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <XeroCard statusQ={statusQ} addToast={addToast} />
        <RuleCard addToast={addToast} />
      </div>
      <MappingCard accountsQ={accountsQ} addToast={addToast} />
    </>
  )
}
